import { hiddenModeRefusal } from "../validations";
import { readHidden, writeHidden } from "../visibility";
import type { CustomizationHandler } from "./types";

/**
 * The storefront's installments panel: the payments block of the product
 * detail, which opens the "payment methods" modal when tapped.
 *
 * `.js-product-payments-container` is in all 24 Core themes that carry the
 * SDK, and only on the product detail. That last part is why it is the
 * selector rather than the narrower `.js-max-installments-container`: the
 * shared installments component renders the "3x $33,33" line inside product
 * grid items and the cart too, so binding to it would hide the installments
 * of every related-product card on the page along with the panel. The
 * container is a `<div>` in every theme, so there is no per-instance
 * narrowing here.
 *
 * It wraps the payment-discount line, the installments line, the card logos
 * and the `#btn-installments` link, which is the block a merchant means by
 * "no installments on the product page".
 *
 * Only `hidden` for now: the label inside is the theme's own wording of a
 * price the store did not choose, so `text` is not offered.
 *
 * @see ./README.md
 */
export const productDetailInstallments: CustomizationHandler<"product-detail-installments"> =
	{
		host: "storefront",
		selector: ".js-product-payments-container",

		read(element) {
			// The themes render this container with an inline `display: none` when
			// the product has no installments to show, and that is deliberately
			// not part of the snapshot: the SDK never writes `display` here, so it
			// has nothing of the theme's to give back. See ../visibility.ts.
			return { hidden: readHidden(element) };
		},

		operation(element, options) {
			if (options.hidden !== undefined) writeHidden(element, options.hidden);
		},

		values(element) {
			return {
				// The SDK's own mode, like `read`: the theme's `display` is not ours
				// to report, see ../visibility.ts.
				hidden: readHidden(element),
			};
		},

		validate(_element, options) {
			if (options.hidden !== undefined) {
				const refusal = hiddenModeRefusal(options.hidden);
				if (refusal) return `refused "hidden": ${refusal}`;
			}

			return null;
		},
	};
